import { useState, useEffect } from "react";
import { usePathname } from "next/navigation";
import styles from "./index.module.scss";
import { COUNTRIES_DATA } from "@/constants";
import { FiChevronDown } from "react-icons/fi";

const marketSelectionLabel = "Select Market";

const MarketAvailability = ({
  availableMarkets = [],
  fetchMarketBasedProducts = () => {},
}) => {
  const pathname = usePathname();

  const availableMarketsList = availableMarkets.length
    ? COUNTRIES_DATA.filter(country =>
        availableMarkets.includes(`KohlerPlumbing${country.value}`)
      )
    : [];

  const [currentMode, setCurrentMode] = useState("All Products");
  const [activeCountry, setActiveCountry] = useState(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  // Parse initial state from URL on first mount only
  useEffect(() => {
    if (typeof window !== "undefined") {
      const params = new URLSearchParams(window.location.search);
      const urlCountry = params.get("country");

      if (urlCountry) {
        setActiveCountry(urlCountry);
        setCurrentMode("Specific Market");
      } else {
        setActiveCountry(null);
        setCurrentMode("All Products");
      }
    }

    const handleCloseSignal = () => {
      setIsDropdownOpen(false);
    };

    window.addEventListener("close-market-dropdown", handleCloseSignal);

    return () => {
      window.removeEventListener("close-market-dropdown", handleCloseSignal);
    };
  }, []);

  // Sync state values with body scrolling safely on mobile viewports
  useEffect(() => {
    if (isDropdownOpen && window.innerWidth <= 768) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isDropdownOpen]);

  // FIX: Native window pushState prevents Next.js layout resets and flash cycles
  const updateUrlParamNative = countryValue => {
    if (typeof window !== "undefined") {
      const params = new URLSearchParams(window.location.search);

      if (countryValue) {
        params.set("country", countryValue);
      } else {
        params.delete("country");
      }

      const newUrl = `${pathname}?${params.toString()}`;
      window.history.pushState(
        { ...window.history.state, as: newUrl, url: newUrl },
        "",
        newUrl
      );
      fetchMarketBasedProducts();
    }
  };

  const handleAllProductsClick = () => {
    setCurrentMode("All Products");
    setIsDropdownOpen(false);
    setActiveCountry(null);
    updateUrlParamNative(null);
  };

  const handleMarketModeClick = e => {
    e.preventDefault();
    setCurrentMode("Specific Market");
    setIsDropdownOpen(prev => !prev);
  };

  const handleCountryClick = (e, value) => {
    e.preventDefault();
    setActiveCountry(value);
    setCurrentMode("Specific Market");
    setIsDropdownOpen(false);
    updateUrlParamNative(value);
  };

  const currentMarketLabel =
    availableMarketsList.find(c => c.value === activeCountry)?.label ||
    marketSelectionLabel;
  const showListPanel = currentMode === "Specific Market" && isDropdownOpen;

  return (
    <div className={styles.marketContainer}>
      <div className="koh-market-title">Market Availability</div>

      <div className="koh-controls-row-wrapper">
        <div className="koh-controls-bar">
          {/* ALL PRODUCTS RADIO */}
          <label className="koh-radio-label">
            <input
              type="radio"
              name="market"
              value="All Products"
              checked={currentMode === "All Products"}
              onChange={handleAllProductsClick}
            />
            <span className="koh-market-values-click">All Products</span>
          </label>

          {/* DYNAMIC SELECT MARKET RADIO */}
          <div
            className={`koh-market-values ${showListPanel ? "market-visible" : ""}`}
          >
            <label className="koh-radio-label">
              <input
                type="radio"
                name="market"
                value="Specific Market"
                checked={currentMode === "Specific Market"}
                onChange={handleMarketModeClick}
              />
              <span
                className="koh-market-values-click"
                onClick={handleMarketModeClick}
              >
                {currentMarketLabel}
                <span className="koh-market-drops">
                  <FiChevronDown size={16} />
                </span>
              </span>
            </label>
          </div>
        </div>

        {/* REGION SELECTION PANELS */}
        <div className={`market-list ${showListPanel ? "active" : ""}`}>
          {/* Mobile Only Header Element */}
          <div className="market-header-mobile">
            <h2>MARKET AVAILABILITY</h2>
            <button
              className="koh-close-btn"
              onClick={() => setIsDropdownOpen(false)}
            >
              &times;
            </button>
          </div>

          <h2 className="desktop-title">Location</h2>

          <div className="market-grid">
            {availableMarketsList.map(country => {
              const isSelected = activeCountry === country.value;
              return (
                <div className="market-col" key={country.value}>
                  <a
                    className={`koh-country-name ${isSelected ? "koh-market-selected" : ""}`}
                    href="#"
                    onClick={e => handleCountryClick(e, country.value)}
                  >
                    <span className="market-name">
                      <span className="flag-wrapper">
                        <img
                          src={country.img}
                          className={
                            country.hasBorder ? "koh-country-logo-border" : ""
                          }
                          alt={country.label}
                        />
                      </span>
                      <span className="label-text">{country.label}</span>
                    </span>
                  </a>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MarketAvailability;
